/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref } from 'preact';
import { ChartMarqueeSelectIcon } from '#PRIVATE_ThemedIcons/ChartMarqueeSelectIcon';
import { ChartPanIcon } from '#PRIVATE_ThemedIcons/ChartPanIcon';
import { styles } from './themes/useVisDragModeControls.css';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { ChartZoomIcon } from '#PRIVATE_ThemedIcons/ChartZoomIcon';
import { ButtonSetSingle } from '#UNSAFE_ButtonSetSingle';
import { ButtonSetItem } from '#UNSAFE_ButtonSetItem';
import { IconToggleButton } from '#UNSAFE_IconToggleButton';

type Props = {
  divRef: Ref<HTMLDivElement>;
  position: {
    top?: string;
    bottom?: string;
    right?: string;
    left?: string;
  };
  dragMode?: 'select' | 'zoom' | 'pan';
  showZoomButton: boolean;
  showPanButton: boolean;
  showSelectButton: boolean;
  onDragModeChange: (mode?: 'select' | 'zoom' | 'pan') => void;
};

export function VisDragModeControls({
  position,
  divRef,
  dragMode,
  onDragModeChange,
  showZoomButton,
  showPanButton,
  showSelectButton
}: Props) {
  const { right, left, top, bottom } = position;
  const onButtonAction = ({ value }: { value: 'zoom' | 'pan' | 'select' }) => {
    onDragModeChange(value === dragMode ? undefined : value);
  };

  const showOnlyOne =
    (showPanButton && !showSelectButton && !showZoomButton) ||
    (!showPanButton && !showSelectButton && showZoomButton) ||
    (!showPanButton && showSelectButton && !showZoomButton);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const zoomAriaLabel = translations.vis_marqueeZoomTooltip();
  const panAriaLabel = translations.vis_panTooltip();
  const selectAriaLabel = translations.vis_marqueeSelectTooltip();
  return showZoomButton || showPanButton || showSelectButton ? (
    <div
      ref={divRef}
      class={styles.background}
      data-oj-private-object="dragModeControls"
      style={{
        right,
        left,
        top,
        bottom
      }}>
      {!showOnlyOne ? (
        <ButtonSetSingle
          size="sm"
          value={dragMode}
          onCommit={onButtonAction as (detail: { value?: string }) => void}
          display="icons"
          variant="outlined">
          {showPanButton && (
            <ButtonSetItem
              value="pan"
              label={panAriaLabel}
              startIcon={<ChartPanIcon />}></ButtonSetItem>
          )}
          {showZoomButton && (
            <ButtonSetItem
              value="zoom"
              label={zoomAriaLabel}
              startIcon={<ChartZoomIcon />}></ButtonSetItem>
          )}
          {showSelectButton && (
            <ButtonSetItem
              value="select"
              label={selectAriaLabel}
              startIcon={<ChartMarqueeSelectIcon />}></ButtonSetItem>
          )}
        </ButtonSetSingle>
      ) : (
        <>
          {showPanButton && (
            <IconToggleButton
              accessibleLabel={panAriaLabel}
              isSelected={dragMode === 'pan'}
              onToggle={() => {
                onDragModeChange(dragMode === 'pan' ? undefined : 'pan');
              }}
              variant="outlined"
              size="sm">
              <ChartPanIcon />
            </IconToggleButton>
          )}
          {showZoomButton && (
            <IconToggleButton
              accessibleLabel={zoomAriaLabel}
              isSelected={dragMode === 'zoom'}
              onToggle={() => {
                onDragModeChange(dragMode === 'zoom' ? undefined : 'zoom');
              }}
              variant="outlined"
              size="sm">
              <ChartZoomIcon />
            </IconToggleButton>
          )}
          {showSelectButton && (
            <IconToggleButton
              accessibleLabel={selectAriaLabel}
              isSelected={dragMode === 'select'}
              onToggle={() => {
                onDragModeChange(dragMode === 'select' ? undefined : 'select');
              }}
              variant="outlined"
              size="sm">
              <ChartMarqueeSelectIcon />
            </IconToggleButton>
          )}
        </>
      )}
    </div>
  ) : null;
}
